/**
 * Chart Service Layer
 *
 * API 호출 및 Domain 모델 변환을 담당
 * Layered Architecture 패턴 적용
 *
 * 새로운 개별 Chart 엔드포인트 사용
 */

import type {
  AverageWeightChartApiResponse,
  SampleWeightChartApiResponse,
  FinalCountChartApiResponse,
  BiomassChartApiResponse,
  WeightHistogramApiResponse,
  FeedingAmountChartApiResponse,
  TankWaterQualityChartApiResponse,
  BaseChartApiDto,
} from '@/types/api'

import { getData } from '@/utils'

// ============================================
// Domain Types
// ============================================

/**
 * 차트 데이터 포인트
 */
export interface ChartDataPoint {
  x: number
  y: number
}

/**
 * 기본 차트 응답 (Domain)
 */
export interface BaseChartData {
  xMin: number
  xMax: number
  yMin: number
  yMax: number
  xTicks: number[]
  yTicks: number[]
  xyPointsPerSchoolId: Record<string, ChartDataPoint[]>
}

/**
 * 히스토그램 응답 (Domain)
 */
export interface HistogramData {
  labels: number[]
  histogramPerSchoolId: Record<string, number[]>
}

/**
 * 수질 차트 응답 (Domain)
 * NH3와 NO2 두 개의 차트 데이터를 포함
 */
export interface WaterQualityChartData {
  nh3: BaseChartData
  no2: BaseChartData
}

// ============================================
// Mappers
// ============================================

/**
 * API 응답을 Domain 모델로 변환
 * 모든 차트 API 응답은 동일한 구조 (BaseChartApiDto)를 공유
 */
function toBaseChartData(apiResponse: BaseChartApiDto): BaseChartData {
  return {
    xMin: apiResponse.x_min,
    xMax: apiResponse.x_max,
    yMin: apiResponse.y_min,
    yMax: apiResponse.y_max,
    xTicks: apiResponse.x_ticks,
    yTicks: apiResponse.y_ticks,
    xyPointsPerSchoolId: Object.fromEntries(
      Object.entries(apiResponse.xy_points_per_school_id).map(([schoolId, points]) => [
        schoolId,
        points.map((p) => ({ x: p.x ?? 0, y: p.y ?? 0 })),
      ])
    ),
  }
}

/**
 * 히스토그램 API 응답을 Domain 모델로 변환
 */
function toHistogramData(apiResponse: WeightHistogramApiResponse): HistogramData {
  // ✅ API 응답 유효성 검사 추가
  if (!apiResponse) {
    console.warn('[toHistogramData] apiResponse is null or undefined')
    return {
      labels: [],
      histogramPerSchoolId: {},
    }
  }

  return {
    labels: apiResponse.labels || [],
    histogramPerSchoolId: apiResponse.histogram_per_school_id || {},
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * school_id 쿼리 파라미터 생성
 */
function buildSchoolIdQuery(schoolIds: string[]): string {
  return schoolIds.map((id) => `school_id=${id}`).join('&')
}

// ============================================
// Chart Queries
// ============================================

/**
 * 평균 무게 차트 조회
 *
 * @param schoolIds - School ID 배열
 * @returns BaseChartData
 */
export async function getAverageWeightChart(schoolIds: string[]): Promise<BaseChartData> {
  try {
    const query = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<AverageWeightChartApiResponse>(
      `/chart/average-weight?${query}`
    )
    return toBaseChartData(apiResponse)
  } catch (error) {
    console.error('Failed to fetch average weight chart:', error)
    throw error
  }
}

/**
 * 샘플 무게 차트 조회
 *
 * @param schoolIds - School ID 배열
 * @returns BaseChartData
 */
export async function getSampleWeightChart(schoolIds: string[]): Promise<BaseChartData> {
  try {
    const query = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<SampleWeightChartApiResponse>(
      `/chart/sample-weight?${query}`
    )
    return toBaseChartData(apiResponse)
  } catch (error) {
    console.error('Failed to fetch sample weight chart:', error)
    throw error
  }
}

/**
 * 최종 마릿수 차트 조회
 *
 * @param schoolIds - School ID 배열
 * @returns BaseChartData
 */
export async function getFinalCountChart(schoolIds: string[]): Promise<BaseChartData> {
  try {
    const query = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<FinalCountChartApiResponse>(`/chart/final-count?${query}`)
    return toBaseChartData(apiResponse)
  } catch (error) {
    console.error('Failed to fetch final count chart:', error)
    throw error
  }
}

/**
 * 바이오매스 차트 조회
 *
 * @param schoolIds - School ID 배열
 * @returns BaseChartData
 */
export async function getBiomassChart(schoolIds: string[]): Promise<BaseChartData> {
  try {
    const query = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<BiomassChartApiResponse>(`/chart/biomass?${query}`)
    return toBaseChartData(apiResponse)
  } catch (error) {
    console.error('Failed to fetch biomass chart:', error)
    throw error
  }
}

/**
 * 급이량 차트 조회
 *
 * Note: Backend uses same DTO as Biomass (GetBiomassChartResDto) by design.
 * Both charts share identical data structure.
 *
 * @param schoolIds - School ID 배열
 * @returns BaseChartData
 */
export async function getFeedingAmountChart(schoolIds: string[]): Promise<BaseChartData> {
  try {
    const query = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<FeedingAmountChartApiResponse>(
      `/chart/feeding-amount?${query}`
    )
    return toBaseChartData(apiResponse)
  } catch (error) {
    console.error('Failed to fetch feeding amount chart:', error)
    throw error
  }
}

/**
 * 무게 히스토그램 조회
 *
 * @param schoolIds - School ID 배열
 * @param date - 조회 날짜 (YYYY-MM-DD)
 * @returns HistogramData
 */
export async function getWeightHistogram(
  schoolIds: string[],
  date: string
): Promise<HistogramData> {
  try {
    const schoolIdQuery = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<WeightHistogramApiResponse>(
      `/chart/weight-histogram?${schoolIdQuery}&date=${date}`
    )
    return toHistogramData(apiResponse)
  } catch (error) {
    console.error('Failed to fetch weight histogram:', error)
    throw error
  }
}

/**
 * 수질 차트 조회 (NH3 & NO2)
 *
 * @param schoolIds - School ID 배열
 * @returns WaterQualityChartData (nh3, no2 두 개의 차트 데이터)
 */
export async function getTankWaterQualityChart(
  schoolIds: string[]
): Promise<WaterQualityChartData> {
  try {
    const query = buildSchoolIdQuery(schoolIds)
    const apiResponse = await getData<TankWaterQualityChartApiResponse>(
      `/chart/tank-water-quality?${query}`
    )
    return {
      nh3: toBaseChartData(apiResponse.nh3),
      no2: toBaseChartData(apiResponse.no2),
    }
  } catch (error) {
    console.error('Failed to fetch tank water quality chart:', error)
    throw error
  }
}

// ============================================
// Property-based Chart Fetcher
// ============================================

export type ChartProperty =
  | 'averageWeight'
  | 'sampleWeight'
  | 'feedingAmount'
  | 'count'
  | 'biomass'

/**
 * Property 이름에 따라 적절한 차트 데이터 조회
 *
 * @param property - 차트 속성 이름
 * @param schoolIds - School ID 배열
 * @returns BaseChartData
 */
export async function getChartByProperty(
  property: ChartProperty,
  schoolIds: string[]
): Promise<BaseChartData> {
  switch (property) {
    case 'averageWeight':
      return getAverageWeightChart(schoolIds)
    case 'sampleWeight':
      return getSampleWeightChart(schoolIds)
    case 'feedingAmount':
      return getFeedingAmountChart(schoolIds)
    case 'count':
      return getFinalCountChart(schoolIds)
    case 'biomass':
      return getBiomassChart(schoolIds)
    default:
      throw new Error(`Unknown chart property: ${property}`)
  }
}

/**
 * 여러 속성의 차트 데이터를 병렬로 조회
 *
 * @param properties - 차트 속성 이름 배열
 * @param schoolIds - School ID 배열
 * @returns Record<ChartProperty, BaseChartData>
 */
export async function getMultipleCharts(
  properties: ChartProperty[],
  schoolIds: string[]
): Promise<Record<ChartProperty, BaseChartData>> {
  const results = await Promise.all(
    properties.map(async (property) => {
      const data = await getChartByProperty(property, schoolIds)
      return [property, data] as const
    })
  )

  return Object.fromEntries(results) as Record<ChartProperty, BaseChartData>
}
